import React, { useEffect, useState, useRef } from "react";
import styles from "./Members.module.css";
import { getMembers, searchSuggestions } from "../services/member";
import { useNavigate } from "react-router-dom";
import MemberCard from "../components/MemberCard";
import { Search, Tag, X } from "lucide-react";
import BniLoadingSpinner from "../components/BniLoadingAnimation";

// --------------------------------------
//  CACHE HELPERS
// --------------------------------------

function loadMembersFromCache() {
  try {
    const raw = localStorage.getItem("members_page1_default");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.error("Cache read failed", err);
    return null;
  }
}

function saveMembersToCache(data: any) {
  try {
    localStorage.setItem("members_page1_default", JSON.stringify(data));
  } catch (err) {
    console.error("Cache save failed", err);
  }
}

export default function Members() {
  const [members, setMembers] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [searchBusiness, setSearchBusiness] = useState("");

  const [nameSuggestions, setNameSuggestions] = useState<any[]>([]);
  const [businessSuggestions, setBusinessSuggestions] = useState<any[]>([]);

  const [loadingNameSuggest, setLoadingNameSuggest] = useState(false);
  const [loadingBusinessSuggest, setLoadingBusinessSuggest] = useState(false);

  const [activeBox, setActiveBox] = useState<"name" | "business" | null>(null);

  const nav = useNavigate();
  const API = process.env.REACT_APP_API_URL || "http://localhost:8080";

  const nameRef = useRef<HTMLDivElement | null>(null);
  const businessRef = useRef<HTMLDivElement | null>(null);

  // --------------------------------------
  // Close dropdown on outside click
  // --------------------------------------
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideName = nameRef.current?.contains(target);
      const insideBusiness = businessRef.current?.contains(target);
      if (!insideName && !insideBusiness) {
        setActiveBox(null);
        setNameSuggestions([]);
        setBusinessSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // --------------------------------------
  // Load cached members INSTANTLY (only page 1, no filters)
  // --------------------------------------
  useEffect(() => {
    if (page === 1 && !search && !searchBusiness) {
      const cached = loadMembersFromCache();
      if (cached?.data) {
        setMembers(cached.data || []);
        setTotalPages(cached.pagination?.totalPages || 1);
      }
    }
  }, []);

  // --------------------------------------
  // Load members from API or fallback
  // --------------------------------------
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);

        const res = await getMembers(page, pageSize, search, searchBusiness);

        if (!mounted) return;

        if (res) {
          setMembers(res.data || []);
          setTotalPages(res.pagination?.totalPages || 1);

          // Save default page to cache
          if (page === 1 && !search && !searchBusiness) {
            saveMembersToCache(res);
          }
        } else {
          // Fallback: empty state
          setMembers([]);
        }
      } catch (err) {
        console.error("Load members failed", err);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [page, search, searchBusiness]);

  // --------------------------------------
  // Debounced name suggestions
  // --------------------------------------
  useEffect(() => {
    if (activeBox !== "name") return;
    if (!search) {
      setNameSuggestions([]);
      return;
    }

    const t = setTimeout(async () => {
      try {
        setLoadingNameSuggest(true);
        const data = await searchSuggestions(search, "name");
        setNameSuggestions(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingNameSuggest(false);
      }
    }, 260);

    return () => clearTimeout(t);
  }, [search, activeBox]);

  // --------------------------------------
  // Debounced business suggestions
  // --------------------------------------
  useEffect(() => {
    if (activeBox !== "business") return;
    if (!searchBusiness) {
      setBusinessSuggestions([]);
      return;
    }

    const t = setTimeout(async () => {
      try {
        setLoadingBusinessSuggest(true);
        const data = await searchSuggestions(searchBusiness, "business");
        setBusinessSuggestions(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingBusinessSuggest(false);
      }
    }, 260);

    return () => clearTimeout(t);
  }, [searchBusiness, activeBox]);

  // --------------------------------------
  // Name selection → open profile
  // --------------------------------------
  const handleNameSelect = (m: any) => {
    if (!m?._id) return;
    setSearch(m.name);
    setNameSuggestions([]);
    setActiveBox(null);
    setPage(1);
    nav(`/member/${m._id}`);
  };

  // --------------------------------------
  // Business category select → filter
  // --------------------------------------
  const handleBusinessSelect = (cat: string) => {
    setSearchBusiness(cat);
    setBusinessSuggestions([]);
    setActiveBox(null);
    setPage(1);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    type: "name" | "business"
  ) => {
    if (e.key === "Enter") {
      setActiveBox(null);
      setNameSuggestions([]);
      setBusinessSuggestions([]);
      setPage(1);
    }
  };

  const clearSearch = () => {
    setSearch("");
    setNameSuggestions([]);
    setPage(1);
  };

  const clearBusiness = () => {
    setSearchBusiness("");
    setBusinessSuggestions([]);
    setPage(1);
  };

  // --------------------------------------
  // UI
  // --------------------------------------
  return (
    <div className={styles.wrapper}>
      <h2 className={styles.heading}>BNI Brisk Members</h2>

      {/* Filters */}
      <div className={styles.filtersWrapper}>
        <div
          className={`${styles.filtersInner} ${
            activeBox === "name" ? styles.hideBusinessOnMobile : ""
          }`}
        >
          {/* Name Filter */}
          <div className={styles.filterBoxNeo} ref={nameRef}>
            <div className={styles.leftIcon}>
              <Search size={16} />
            </div>

            <input
              className={styles.filterInput}
              placeholder="Search member name..."
              value={search}
              onFocus={() => setActiveBox("name")}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveBox("name");
                setPage(1);
              }}
              onKeyDown={(e) => handleKeyDown(e, "name")}
            />

            {search && (
              <button className={styles.clearBtn} onClick={clearSearch}>
                <X size={16} />
              </button>
            )}

            {activeBox === "name" && (
              <div className={styles.suggestionPanel}>
                {loadingNameSuggest && (
                  <div className={styles.loadingText}>Loading...</div>
                )}

                {!loadingNameSuggest &&
                  nameSuggestions.length === 0 &&
                  search.length > 0 && (
                    <div className={styles.noSuggest}>No matching members</div>
                  )}

                {!loadingNameSuggest &&
                  nameSuggestions.map((s) => (
                    <div
                      key={s._id}
                      className={styles.suggestionItemNeo}
                      onMouseDown={() => handleNameSelect(s)}
                    >
                      <div className={styles.suggestionName}>{s.name}</div>
                      <div className={styles.suggestionMeta}>
                        {s.businessCategory || "—"}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Business Filter */}
          <div
            className={`${styles.filterBoxNeo} ${styles.businessFilter}`}
            ref={businessRef}
          >
            <div className={styles.leftIcon}>
              <Tag size={16} />
            </div>

            <input
              className={styles.filterInput}
              placeholder="Search business category..."
              value={searchBusiness}
              onFocus={() => setActiveBox("business")}
              onChange={(e) => {
                setSearchBusiness(e.target.value);
                setActiveBox("business");
                setPage(1);
              }}
              onKeyDown={(e) => handleKeyDown(e, "business")}
            />

            {searchBusiness && (
              <button className={styles.clearBtn} onClick={clearBusiness}>
                <X size={16} />
              </button>
            )}

            {activeBox === "business" && (
              <div className={styles.suggestionPanel}>
                {loadingBusinessSuggest && (
                  <div className={styles.loadingText}>Loading...</div>
                )}

                {!loadingBusinessSuggest &&
                  businessSuggestions.length === 0 &&
                  searchBusiness.length > 0 && (
                    <div className={styles.noSuggest}>No categories found</div>
                  )}

                {!loadingBusinessSuggest &&
                  businessSuggestions.map((s: any, i: number) => {
                    const cat = s.businessCategory ?? s._id ?? s;
                    return (
                      <div
                        key={`${cat}-${i}`}
                        className={styles.suggestionItemNeo}
                        onMouseDown={() => handleBusinessSelect(cat)}
                      >
                        <div className={styles.suggestionName}>{cat}</div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className={styles.loadingBox}>
          <BniLoadingSpinner />
        </div>
      ) : members.length === 0 ? (
        <div className={styles.noResultsBox}>
          <h3>No Members Found</h3>
          <p>Try clearing filters.</p>
          <button
            className={styles.resetBtn}
            onClick={() => {
              setSearch("");
              setSearchBusiness("");
              setPage(1);
            }}
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <div className={styles.grid}>
          {members.map((m) => (
            <MemberCard key={m._id} member={m} api={API} />
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className={styles.pagination}>
        <button
          disabled={page === 1}
          className={styles.pageBtn}
          onClick={() => setPage((p) => p - 1)}
        >
          Prev
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => setPage(i + 1)}
            className={`${styles.pageNumber} ${
              page === i + 1 ? styles.active : ""
            }`}
          >
            {i + 1}
          </button>
        ))}

        <button
          disabled={page === totalPages}
          className={styles.pageBtn}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
